import { Suspense, useState } from "react";
import { Helmet } from "react-helmet";
import { Bounds, Html, OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";

import AvatarCharacter from "components/AvatarCharacter";
import {
  AVATAR_CHARACTER_LIST,
  DEFAULT_VOICE_CHARACTER_ID,
  getAvatarCharacter,
} from "components/AvatarCharacter/characters";
import "./style.css";

function Loader() {
  return (
    <Html center>
      <div className="avatar-gallery-loader">
        <span />
        Loading VRM
      </div>
    </Html>
  );
}

export default function AvatarGallery() {
  const [selectedId, setSelectedId] = useState(DEFAULT_VOICE_CHARACTER_ID);
  const [expression, setExpression] = useState(null);
  const [targets, setTargets] = useState([]);
  const model = getAvatarCharacter(selectedId);

  function selectModel(id) {
    setSelectedId(id);
    setExpression(null);
    setTargets([]);
  }

  function triggerExpression(nextExpression) {
    setExpression(nextExpression);
    window.setTimeout(() => {
      setExpression((current) => (
        current === nextExpression ? null : current
      ));
    }, nextExpression === "blink" ? 180 : 700);
  }

  return (
    <>
      <Helmet>
        <title>Open-Source Avatar Gallery — AIHumanity</title>
        <meta
          name="description"
          content="Compare open-source VRM avatars and preview facial expressions and lip-sync shapes."
        />
      </Helmet>

      <div className="avatar-gallery-page">
        <nav className="avatar-gallery-nav">
          <a href="/" className="avatar-gallery-brand">AIHumanity</a>
          <div>
            <a href="/demo/voice-agent">Voice agent</a>
            <a href="/demo">All demos</a>
          </div>
        </nav>

        <main className="avatar-gallery-main">
          <header className="avatar-gallery-header">
            <span>CC0 · Interactive VRM comparison</span>
            <h1>Choose a face for the voice.</h1>
            <p>
              Drag to rotate. Scroll to zoom. Test vowel shapes, blinking,
              and available expressions before choosing the demo avatar.
            </p>
          </header>

          <section className="avatar-gallery-workbench">
            <div className="avatar-gallery-stage">
              <Canvas
                camera={{ position: [0, 1.2, 4.6], fov: 32 }}
                dpr={[1, 1.75]}
                gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
              >
                <color attach="background" args={["#0a1020"]} />
                <fog attach="fog" args={["#0a1020", 6, 12]} />
                <ambientLight intensity={2.2} />
                <directionalLight position={[3, 5, 4]} intensity={3.5} color="#dff8ff" />
                <directionalLight position={[-4, 2, -2]} intensity={2.2} color="#ff9f78" />
                <pointLight position={[0, 1, 3]} intensity={4} color="#64dcff" />

                <Suspense fallback={<Loader />}>
                  <Bounds key={model.id} fit clip observe margin={1.08}>
                    <AvatarCharacter
                      character={model}
                      expression={expression}
                      onTargets={setTargets}
                      framing={{
                        position: [0, 0, 0],
                        rotation: [0, Math.PI, 0],
                        scale: 1,
                      }}
                    />
                  </Bounds>
                </Suspense>
                <OrbitControls
                  makeDefault
                  enableDamping
                  minDistance={1.5}
                  maxDistance={8}
                  target={[0, 1, 0]}
                />
              </Canvas>

              <div className="avatar-gallery-stage-label">
                <span>Now viewing</span>
                <strong>{model.name}</strong>
              </div>
            </div>

            <aside className="avatar-gallery-panel">
              <div>
                <span className="avatar-gallery-label">Models</span>
                <div className="avatar-gallery-models">
                  {AVATAR_CHARACTER_LIST.map((candidate) => (
                    <button
                      type="button"
                      key={candidate.id}
                      className={candidate.id === model.id ? "selected" : ""}
                      onClick={() => selectModel(candidate.id)}
                    >
                      <img src={candidate.previewUrl} alt="" />
                      <span>
                        <strong>{candidate.name}</strong>
                        <small>{candidate.note}</small>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="avatar-gallery-label">Lip shapes</span>
                <div className="avatar-gallery-actions">
                  {["a", "e", "o"].map((vowel) => (
                    <button
                      type="button"
                      key={vowel}
                      className={expression === vowel ? "active" : ""}
                      onClick={() => triggerExpression(vowel)}
                    >
                      {vowel.toUpperCase()}
                    </button>
                  ))}
                  <button type="button" onClick={() => triggerExpression("blink")}>
                    Blink
                  </button>
                </div>
              </div>

              <div>
                <span className="avatar-gallery-label">Expressions</span>
                <div className="avatar-gallery-actions">
                  {["joy", "sorrow", "angry"].map((emotion) => (
                    <button
                      type="button"
                      key={emotion}
                      disabled={!model.expressions.includes(emotion)}
                      className={expression === emotion ? "active" : ""}
                      onClick={() => triggerExpression(emotion)}
                    >
                      {emotion}
                    </button>
                  ))}
                </div>
              </div>

              <details className="avatar-gallery-debug">
                <summary>Model diagnostics</summary>
                <p>{targets.length} facial morph targets detected</p>
                <code>{targets.join(", ") || "Loading target list…"}</code>
              </details>

              <p className="avatar-gallery-license">
                Models: CC0 via Open Source Avatars. Viewer is for evaluation;
                selected asset should be self-hosted before launch.
              </p>
            </aside>
          </section>
        </main>
      </div>
    </>
  );
}
